// src/services/closingService.js
//
// Closing Statements reconstruidos (2026-09-23): un documento por cada pago
// que se le liquida al cliente, con su breakdown (ver
// documents/closingData.js). Colección nueva `closings`, separada de la
// vieja `closingStatements` (el Paso 2 del wizard anterior, que creaba el
// cheque al firmarse) para no mezclar modelos.
//
// Un reclamo puede tener varios cheques: hay un closing por cada uno. Los
// cheques de la Fase 4 (registrados al emitirse) ligan su closing con
// `checkId`; los closings sueltos anteriores no lo tienen.
//
// Ciclo de cada closing (`status`):
//   draft      guardado
//   generated  se descargó el PDF
//   sent       se mandó a firmar (registro a mano: fecha + enlace opcional)
//   signed     el PDF firmado quedó respaldado en Hostinger (backup.php,
//              tipo closing_statement_signature) — desde acá los montos
//              quedan bloqueados.
import { auth, db } from '../firebase/config';
import { addDoc, collection, doc, getDocs, query, Timestamp, updateDoc, where } from 'firebase/firestore';
import { uploadBackup } from './backupService';
import { createCheckFromClosing } from './claimService';
import { CLOSING_FEES, computeBreakdown } from '../documents/closingData';

const CLOSINGS = 'closings';

const currentEmail = () => auth.currentUser?.email || '';

const amount = v => (v === '' || v === null || v === undefined || Number.isNaN(Number(v)) ? null : Number(v));

// Solo los renglones del documento (closingData.js → CLOSING_FEES).
const clean = closing => ({
  checkAmount: amount(closing.checkAmount),
  feePercent: amount(closing.feePercent),
  paFee: amount(closing.paFee),
  ...Object.fromEntries(CLOSING_FEES.map(fee => [fee.key, amount(closing[fee.key])])),
  checkNumber: (closing.checkNumber || '').trim(),
  checkDate: closing.checkDate || '',
  paymentType: closing.paymentType || '',
  date: closing.date || ''
});

export const CLOSING_STATUSES = ['draft', 'generated', 'sent', 'signed'];

// Orden en memoria (más nuevo primero): así la consulta no necesita un
// índice compuesto nuevo en Firestore.
export const listClosings = async claimRecordId => {
  const snapshot = await getDocs(query(collection(db, CLOSINGS), where('claimRecordId', '==', claimRecordId)));
  const millis = t => (t?.toMillis ? t.toMillis() : 0);
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => millis(b.createdAt) - millis(a.createdAt));
};

// Crea o actualiza. Guarda también el neto calculado para listarlo sin recalcular.
export const saveClosing = async (claimRecord, closing, closingId = null) => {
  if (closingId && closing.status === 'signed') throw new Error('SIGNED_LOCKED');
  const data = clean(closing);
  const { netToClient, paFee } = computeBreakdown(data);
  const now = Timestamp.now();
  const payload = { ...data, paFeeApplied: paFee, netToClient, updatedAt: now, updatedBy: currentEmail() };
  if (closingId) {
    await updateDoc(doc(db, CLOSINGS, closingId), payload);
    return { id: closingId, ...payload };
  }
  const full = {
    ...payload,
    claimRecordId: claimRecord.id,
    checkId: closing.checkId || null,
    companyName: claimRecord.companyName || '',
    adjusterName: claimRecord.adjusterName || '',
    status: 'draft',
    createdAt: now,
    createdBy: currentEmail()
  };
  const ref = await addDoc(collection(db, CLOSINGS), full);
  return { id: ref.id, ...full };
};

const setStatus = async (closingId, fields) => {
  const payload = { ...fields, updatedAt: Timestamp.now(), updatedBy: currentEmail() };
  await updateDoc(doc(db, CLOSINGS, closingId), payload);
  return payload;
};

// Se descargó el PDF: solo avanza un borrador (no retrocede uno enviado o firmado).
export const markClosingGenerated = async closing => (
  closing.status && closing.status !== 'draft' ? {} : setStatus(closing.id, { status: 'generated', generatedAt: Timestamp.now() })
);

export const markClosingSent = async (closingId, { sentDate, signatureUrl }) => setStatus(closingId, {
  status: 'sent',
  sentDate: sentDate || '',
  signatureUrl: (signatureUrl || '').trim()
});

// Respaldo del documento firmado (mismo almacenamiento privado que los
// respaldos de cheques y pagos). Si el closing es de un cheque de la Fase 4
// (`checkId`), el cheque ya existe y no se crea nada. Solo un closing suelto
// anterior crea su cheque al firmarse (createCheckFromClosing, idempotente).
export const attachSignedClosing = async (closing, file, signedDate, claimRecord) => {
  const closingId = closing.id;
  const backup = await uploadBackup({ file, claimId: closingId, invoiceNumber: '', type: 'closing_statement_signature' });
  const signed = await setStatus(closingId, {
    status: 'signed',
    signedDate: signedDate || '',
    signedPdf: {
      storageKey: backup.storageKey,
      name: backup.name || file.name,
      mimeType: backup.mimeType || file.type,
      size: backup.size || file.size,
      uploadedAt: Timestamp.now(),
      uploadedBy: backup.uploadedBy || currentEmail()
    }
  });
  if (closing.checkId) return { ...signed, checkId: closing.checkId };
  // Un closing suelto anterior crea además el cheque de cobro de CSR, y crear cheques es del
  // Super Admin: para los demás roles el closing queda firmado y el cheque lo crea el Super Admin.
  try {
    const check = await createCheckFromClosing(closing, claimRecord, currentEmail());
    await setStatus(closingId, { checkId: check.id });
    return { ...signed, checkId: check.id, check };
  } catch (error) {
    if (error?.code !== 'permission-denied') throw error;
    return { ...signed, checkId: null };
  }
};

// Totales del reclamo (todos sus closings). Se suman en centavos enteros
// para no arrastrar errores de punto flotante (7702.539999… en vez de 7702.54).
export const closingTotals = closings => {
  const cents = v => Math.round((Number(v) || 0) * 100);
  const sum = key => closings.reduce((acc, c) => acc + cents(c[key]), 0) / 100;
  return { received: sum('checkAmount'), paFees: sum('paFeeApplied'), net: sum('netToClient') };
};
